import { z } from "zod";

/**
 * Women-only group trips: the contract shared by the API, web, admin and app.
 *
 * MaJourneys only lists these trips. An organiser runs each one with female
 * staff. The guest reserves a seat, pays a deposit straight to the organiser
 * (bank transfer or CashPlus, never to us), and pays the rest on the day. The
 * team marks "deposit received" in admin, then confirms.
 */

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** A trip date and how many seats are still free on it. */
export interface TripDateDTO {
  date: string;
  /** Null when the trip has no seat limit. */
  seatsLeft: number | null;
}

/** The public face of an organiser: safe for any listing. No payment details. */
export interface OrganiserSummaryDTO {
  slug: string;
  name: string;
  tagline: string | null;
  instagramHandle: string | null;
  avatarUrl: string | null;
}

export interface OrganiserPageDTO extends OrganiserSummaryDTO {
  bio: string | null;
  /** Colours from the logo, light first; the page's top band. Empty: the default pink band. */
  brandColors: string[];
  /** Photos from past outings, shown only with the organiser's OK. */
  galleryUrls: string[];
  /** Every city her upcoming trips leave from, in first-seen order. */
  departures: string[];
  trips: WomenTripCardDTO[];
}

/** Deposit details, shown to a guest only after she has reserved. */
export interface OrganiserPaymentDTO {
  organiserName: string;
  bankName: string | null;
  bankAccountHolder: string | null;
  bankRib: string | null;
  cashplusName: string | null;
  cashplusPhone: string | null;
}

/** One card on /women and on an organiser page. */
export interface WomenTripCardDTO {
  slug: string;
  title: string;
  subtitle: string | null;
  imageUrl: string | null;
  citySlug: string;
  cityName: string;
  categorySlug: string | null;
  /** Lowest seat price, in `currency`, whole: the same number the other cards show. */
  price: number;
  currency: string;
  /** Deposit per seat, in MAD (what the guest sends the organiser). */
  depositAmount: number | null;
  seatLimit: number | null;
  /** Upcoming dates only, soonest first. */
  dates: TripDateDTO[];
  organiser: OrganiserSummaryDTO | null;
  /** Cities the group leaves from; empty when it leaves from one place. */
  departures: string[];
}

/** The women-only block on a booking (thank-you, My bookings, email). */
export interface WomenTripBookingDTO {
  depositAmount: number;
  /** total - deposit, never below 0. */
  restOnTheDay: number;
  depositReceivedAt: string | null;
  /** The departure city she picked, when the trip leaves from several. */
  departure?: string | null;
  payment: OrganiserPaymentDTO | null;
}

/** Seats a party takes: adults and children. Infants ride free, as for minPeople. */
export function seatsForParty(party: { adults?: number | null; children?: number | null }): number {
  return Math.max(0, Math.round(party.adults ?? 0)) + Math.max(0, Math.round(party.children ?? 0));
}

/** Seats still free, or null when the trip has no limit. Never negative. */
export function seatsLeft(seatLimit: number | null | undefined, taken: number): number | null {
  if (seatLimit == null || seatLimit <= 0) return null;
  return Math.max(0, seatLimit - taken);
}

/**
 * The deposit for one booking and what is left to pay on the day. The deposit
 * is per seat; it is capped at the total so a cheap trip never shows a deposit
 * larger than its price.
 */
export function depositSplit(
  total: number,
  depositPerSeat: number | null | undefined,
  seats: number,
): { deposit: number; rest: number } | null {
  if (depositPerSeat == null || depositPerSeat <= 0) return null;
  const t = Math.max(0, Math.round(total));
  const deposit = Math.min(t, Math.round(depositPerSeat) * Math.max(1, seats));
  return { deposit, rest: t - deposit };
}

/** Upcoming trip dates (today included), sorted, de-duplicated. */
export function upcomingTripDates(dates: readonly string[], todayIso: string): string[] {
  return [...new Set(dates.filter((d) => ISO_DATE.test(d) && d >= todayIso))].sort();
}

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === "" ? null : v))
    .nullable()
    .optional();

const organiserLocaleSchema = z.object({
  bio: z.string().trim().max(4000).optional(),
  tagline: z.string().trim().max(255).optional(),
});

export const organiserInputSchema = z.object({
  slug: z
    .string()
    .trim()
    .min(2)
    .max(191)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Use lowercase letters, numbers and dashes"),
  name: z.string().trim().min(1).max(191),
  tagline: optionalText(255),
  bio: optionalText(4000),
  translations: z
    .object({
      fr: organiserLocaleSchema.optional(),
      ar: organiserLocaleSchema.optional(),
      es: organiserLocaleSchema.optional(),
      it: organiserLocaleSchema.optional(),
      de: organiserLocaleSchema.optional(),
      nl: organiserLocaleSchema.optional(),
    })
    .default({}),
  instagramHandle: z
    .string()
    .trim()
    .max(120)
    .transform((v) => (v.replace(/^@/, "") === "" ? null : v.replace(/^@/, "")))
    .nullable()
    .optional(),
  avatarUrl: optionalText(2000),
  brandColors: z
    .array(z.string().regex(/^#[0-9a-fA-F]{6}$/))
    .max(3)
    .optional(),
  galleryUrls: z.array(z.string().trim().url().max(2000)).max(12).optional(),
  bankName: optionalText(120),
  bankAccountHolder: optionalText(191),
  bankRib: optionalText(64),
  cashplusName: optionalText(191),
  cashplusPhone: optionalText(64),
});
export type OrganiserInput = z.infer<typeof organiserInputSchema>;
export const organiserPatchSchema = organiserInputSchema.partial();

/** Admin row: everything, payment details included. */
export interface OrganiserAdminDTO extends OrganiserPaymentDTO {
  id: string;
  slug: string;
  name: string;
  tagline: string | null;
  bio: string | null;
  translations: Record<string, { bio?: string; tagline?: string }>;
  instagramHandle: string | null;
  avatarUrl: string | null;
  brandColors: string[];
  galleryUrls: string[];
  tripsCount: number;
  createdAt: string;
  updatedAt: string;
}

/** The women-only fields on an activity, as the admin form sends them. */
export const womenTripFieldsSchema = z.object({
  womenOnly: z.boolean().optional(),
  organiserId: z.string().uuid().nullable().optional(),
  seatLimit: z.number().int().min(1).max(500).nullable().optional(),
  depositAmount: z.number().int().min(0).max(100000).nullable().optional(),
  tripDates: z.array(z.string().regex(ISO_DATE)).max(200).optional(),
  tripDepartures: z.array(z.string().trim().min(1).max(120)).max(10).optional(),
});
export type WomenTripFields = z.infer<typeof womenTripFieldsSchema>;

export * from "./partner";
